import { SEARCH_SORTS, type SearchSort } from '@powerful/types';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../icons/icon';
import { OverlayDialog } from './overlay-dialog';

/**
 * Ordenação (C8): radiogroup NATIVO dentro de um diálogo — as setas percorrem as opções como em
 * qualquer grupo de rádios, e aplicar é explícito (as setas disparam `change`; aplicar a cada seta
 * geraria uma pesquisa e uma entrada no histórico por tecla).
 *
 * «Distância» sem origem continua escolhível: a página pede a localização em vez de fingir.
 */
export interface SortMenuProps {
  value: SearchSort;
  onChange: (sort: SearchSort) => void;
  hasOrigin: boolean;
}

export function SortMenu({ value, onChange, hasOrigin }: SortMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const name = useId();

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  return (
    <>
      <button className="btn btn-ghost" type="button" aria-haspopup="dialog" onClick={() => setOpen(true)} data-testid="sort-button">
        <Icon name="sort" size={18} />
        <span className="pc-vh">{t('discovery.sort.label')}: </span>
        <span>{t(`discovery.sort.options.${value}`)}</span>
      </button>
      <OverlayDialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('discovery.sort.title')}
        testId="sort-menu"
        footer={
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => {
              if (draft !== value) onChange(draft);
              setOpen(false);
            }}
          >
            {t('discovery.sort.apply')}
          </button>
        }
      >
        <fieldset className="pc-fs">
          <legend className="pc-vh">{t('discovery.sort.title')}</legend>
          <ul className="pc-cities">
            {SEARCH_SORTS.map((sort) => (
              <li key={sort}>
                <label className="pc-city">
                  <span className="flex min-w-0 flex-col">
                    <span>{t(`discovery.sort.options.${sort}`)}</span>
                    {sort === 'distance' && !hasOrigin ? <span className="pc-city-sub">{t('discovery.sort.needsOrigin')}</span> : null}
                  </span>
                  <span className="hit -mr-[11px]">
                    <input className="pc-vh" type="radio" name={name} value={sort} checked={draft === sort} onChange={() => setDraft(sort)} />
                    <span className={draft === sort ? 'radio on' : 'radio'} aria-hidden="true" />
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      </OverlayDialog>
    </>
  );
}
